import { ReactNode } from 'react';
import Image from 'next/image';

import { Box, Divider, Grid, GridItem, Heading, Text, useToken } from '@chakra-ui/react';

import { SanityImage } from '~/components/sanity-image';
import { SanityImageType } from '~/features/page-template/models/image';

import { EnterpriseRelatedCompany } from '../models/shared-models';

export const formSlotWidth = '448px';

export function EnterpriseResourceHeroWithLeadForm({
  title,
  description,
  resourceTypeTitle,
  startDate,
  startTime,
  relatedCompanies,
  image,
  formSlot,
  contentSlot,
  bottomLeftSlot,
}: {
  title: string;
  description: string;
  resourceTypeTitle: string;
  startDate?: string | null;
  startTime?: string;
  relatedCompanies: EnterpriseRelatedCompany[] | null;
  image?: SanityImageType | null;
  formSlot: ReactNode;
  contentSlot?: ReactNode;
  bottomLeftSlot?: ReactNode;
}) {
  const [containerWidth] = useToken('sizes', ['content-max-width']);
  const maxHeaderWidth = `calc(${containerWidth} - ${formSlotWidth})`;

  const gridTemplateRows = image ? { base: '1fr', lg: '1fr 0.4fr' } : { base: '1fr', lg: 'min-content 1fr' };
  const baseTemplateAreas = image
    ? {
        base: `
          "top-left header header top-right"
          "middle-left form form middle-right"
          "bottom-left content content bottom-right"
        `,
        lg: `
          "top-left header form top-right"
          "bottom-left header form bottom-right"
        `,
      }
    : {
        base: `
            "top-left header header top-right"
            "middle-left form form middle-right"
            "bottom-left content content bottom-right"
          `,
        lg: `
            "top-left header form top-right"
            "bottom-left content form bottom-right"
          `,
      };

  return (
    <Grid
      gridTemplateRows={gridTemplateRows}
      templateAreas={baseTemplateAreas}
      width='100%'
      gridTemplateColumns={{
        base: '16px 1fr 1fr 16px',
        md: '124px 1fr 1fr 124px',
        lg: `minmax(48px, 1fr) auto ${formSlotWidth} minmax(48px, 1fr)`,
      }}
    >
      <GridItem area={'top-left'} backgroundColor='blue.800' position='relative'>
        <Box backgroundColor='blue.800' height='100%' left='0' position='absolute' top='0' width='100vw' zIndex='-1' />
      </GridItem>
      <GridItem area={'top-right'} backgroundColor='blue.800' />
      <GridItem area={'middle-left'} backgroundColor='blue.800' />
      <GridItem area={'middle-right'} backgroundColor='blue.800' />
      <GridItem area={'bottom-left'}>{bottomLeftSlot}</GridItem>
      <GridItem area={'bottom-right'} />

      <GridItem
        area={'header'}
        height='fit-content'
        maxWidth={{ base: '100%', xl: maxHeaderWidth }}
        minHeight={{ base: '0px', lg: '640px' }}
        paddingBottom={image ? undefined : { base: '0px', lg: '64px' }}
        paddingRight={{ base: '0px', lg: '128px' }}
        paddingTop={{ base: '40px', md: '64px' }}
        position='relative'
      >
        <Box backgroundColor='blue.800'>
          <Box
            backgroundColor='blue.800'
            height='70%'
            left='-100vw'
            position='absolute'
            top='0'
            width='200vw'
            zIndex='-1'
          />

          <Text color='accent.lime' fontSize='16px' fontWeight='600'>
            {resourceTypeTitle}
          </Text>

          <Box display='flex' marginTop='24px'>
            <Image
              alt='Udacity Accenture logo'
              height={22}
              priority={true}
              src='/images/logos/udacity-accenture.svg'
              width={125}
              style={{
                height: 'auto',
                width: 'auto',
              }}
            />
            {relatedCompanies?.map((relatedCompany) => (
              <Box key={relatedCompany._id} display='flex' height='30px'>
                <Divider borderColor='white' marginX='20px' opacity={1} orientation='vertical' />
                <SanityImage image={relatedCompany.lofiLogoLight} style={{ width: 'auto' }} />
              </Box>
            ))}
          </Box>

          <Divider borderColor='accent.sky.500' margin='0px' marginY='40px' opacity={1} />

          <Heading as='h1' color='white' size='marketing-5xl'>
            {title}
          </Heading>

          {startDate && startTime && (
            <Box alignItems='center' display='flex' marginTop='24px'>
              <Text color='accent.lime'>{startDate}</Text>
              <Divider borderColor='accent.sky.500' height='16px' marginX='8px' opacity={1} orientation='vertical' />
              <Text color='accent.lime'>{startTime}</Text>
            </Box>
          )}

          <Text color='white' marginTop='24px'>
            {description}
          </Text>
        </Box>

        {image && (
          <Box paddingTop='40px'>
            <SanityImage
              image={image}
              loading='eager'
              sizes={`(min-width: 992px) ${maxHeaderWidth}, 100vw`}
              style={{
                objectFit: 'contain',
                objectPosition: 'center center',
                width: '100%',
              }}
              priority
            />
          </Box>
        )}
      </GridItem>

      <GridItem
        area={'form'}
        height='fit-content'
        paddingBottom={{ base: '40px', md: '64px', lg: '0px' }}
        paddingTop={{ base: '40px', md: '64px' }}
        position='relative'
      >
        <Box backgroundColor='blue.800' height='120px' left='0' position='absolute' top='0' width='100%' zIndex='-1' />
        <Box
          backgroundColor='blue.800'
          bottom='0'
          display={{ base: 'block', lg: 'none' }}
          height='120px'
          left='0'
          position='absolute'
          width='100%'
          zIndex='-1'
        />
        {formSlot}
      </GridItem>

      <GridItem area={'content'} maxWidth={{ base: '100%', xl: maxHeaderWidth }}>
        {contentSlot}
      </GridItem>
    </Grid>
  );
}
